import React, { useState, useEffect, useCallback } from "react";
import { useRecordContext, useRefresh } from "react-admin";
import { Card, CardContent, Tab, Tabs, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useDataSeamlessApi } from "../../dataSeamless/useDataSeamlessApi";
import { contractedMethodologies, bucketTotalsOf } from "../../dataSeamless/methodologyUtils";
import { isFieldRequired } from "../../dataSeamless/labels";
import { useProjectSeamlessData } from "../../dataSeamless/useProjectSeamlessData";
import ResumenScreen from "../../dataSeamless/screens/ResumenScreen";
import PendientesScreen from "../../dataSeamless/screens/PendientesScreen";
import DatosScreen from "../../dataSeamless/screens/DatosScreen";

const useStyles = makeStyles((theme) => ({
  panel: { marginBottom: theme.spacing(2) },
  count: {
    marginLeft: theme.spacing(1),
    background: theme.palette.grey[200],
    color: theme.palette.text.secondary,
    borderRadius: 10,
    padding: "1px 8px",
    fontSize: 12,
    fontWeight: 700,
  },
}));

// Tab indices — named so navigation between screens reads clearly.
const TAB = { RESUMEN: 0, PENDIENTES: 1, DATOS: 2 };

// Data Seamless embedded inside a project's detail view. The redesign (spec: data-seamless-
// redesign.md) drops the old numbered 4-tab wizard for three task-oriented screens:
// Resumen (¿qué pasa?) · Pendientes (¿qué hago?) · Datos (¿dónde está el problema?).
// The project is the current record, so there is no project selector here.
const DataSeamlessTab = () => {
  const classes = useStyles();
  const record = useRecordContext();
  const refresh = useRefresh();
  const { api } = useDataSeamlessApi();
  const [tab, setTab] = useState(TAB.RESUMEN);
  const [contracted, setContracted] = useState([]);
  // Filter carried across the Resumen → Pendientes deep-link ("Resolver ahora →").
  const [pendientesFilter, setPendientesFilter] = useState(null);
  // Preset carried across the Resumen → Datos deep-link (e.g. click on L1 → Por campo filtered to L1).
  const [datosPreset, setDatosPreset] = useState(null);

  const projectId = record?.id;
  const accountId = record?.accountId;

  const loadFeatures = useCallback(async () => {
    if (!accountId) return;
    const r = await api("GET", `/accountFeatures/${accountId}?limit=200`);
    const items = r.ok ? (Array.isArray(r.data) ? r.data : r.data.data ?? []) : [];
    setContracted(contractedMethodologies(items));
  }, [api, accountId]);

  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  // Shared dataset (completeness + field-level gaps) fetched once for all three screens.
  const data = useProjectSeamlessData(api, projectId);

  // Navigate to another screen, optionally applying a filter/preset (used by Resumen's cards).
  const navigate = useCallback((targetTab, filter = null) => {
    if (targetTab === TAB.PENDIENTES) setPendientesFilter(filter);
    if (targetTab === TAB.DATOS) setDatosPreset(filter);
    setTab(targetTab);
  }, []);

  if (!record?.id) {
    return (
      <Card>
        <CardContent>
          <Typography color="textSecondary">No hay proyecto seleccionado.</Typography>
        </CardContent>
      </Card>
    );
  }

  const projectName = `${record.name || "(sin nombre)"} (${record.id.slice(0, 8)})`;

  // Pending count for the Pendientes tab badge. Only fields REQUIRED (M/M-A) for a contracted
  // methodology count — Recommended-only (R) fields are optional and would otherwise inflate the
  // badge (spec: the screen must speak in "necesario para lo contratado", not raw presence).
  const pendientesCount = (data.gapFields || []).reduce((n, f) => {
    if (!isFieldRequired(f, contracted)) return n;
    const b = bucketTotalsOf(f.statusCounts);
    return n + b.faltan + b.revisar + b.asumibles;
  }, 0);

  return (
    <div>
      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        indicatorColor="primary"
        textColor="primary"
        className={classes.panel}
      >
        <Tab label="Resumen" />
        <Tab
          label={
            <span>
              Pendientes
              {pendientesCount > 0 && <span className={classes.count}>{pendientesCount}</span>}
            </span>
          }
        />
        <Tab label="Datos" />
      </Tabs>

      <div className={classes.panel}>
        {tab === TAB.RESUMEN && (
          <ResumenScreen
            api={api}
            projectId={projectId}
            project={record}
            projectName={projectName}
            contracted={contracted}
            data={data}
            onProjectsChanged={refresh}
            onNavigate={navigate}
            tabs={TAB}
          />
        )}
        {tab === TAB.PENDIENTES && (
          <PendientesScreen
            api={api}
            projectId={projectId}
            contracted={contracted}
            data={data}
            initialFilter={pendientesFilter}
          />
        )}
        {tab === TAB.DATOS && (
          <DatosScreen api={api} projectId={projectId} contracted={contracted} data={data} preset={datosPreset} />
        )}
      </div>
    </div>
  );
};

export default DataSeamlessTab;
